<script setup>
import {ref, reactive, onMounted, onBeforeUnmount, watch, computed, provide} from "vue"
import {useWatchdogStore} from "@/stores/watchdog.js";
import AddOffer from "@/views/watchdog/boost/addOffer.vue";
import AddTracker from "@/views/watchdog/boost/addTracker.vue";
import draggable from "vuedraggable"
import {showMessage} from "@/utils/showMessage.js";
import {useConfirm} from "primevue/useconfirm";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import Button from "@/components/Button/index.vue";
import {Menu} from "primevue";
import FiltersModal from "@/views/watchdog/boost/FiltersModal.vue";
import Breadcrumbs from "@/components/Breadcrumbs/index.vue";
import {Icon} from "@iconify/vue";

//loading flags
const {
  saving,
  deleting,
  setSaving,
  setDeleting,
} = useLoadingFlags()

const watchdogStore = useWatchdogStore();
const confirm = useConfirm();
const loading = ref(false)
const isFetching = ref(false)
const pollInterval = ref(null)
const pendingDragConfirm = ref(false)
const confirmActionTaken = ref(false)
const draggedItem = ref(null)
const dragOldIndex = ref(0)
const isCutSwapPending = ref(false)
const offers = ref([])
const columnsCount = ref(1)
const columns = ref([])
const cutItem = ref(null)
const isShowAddOfferModal = ref(false)
const isShowAddTrackerModal = ref(false)
const mouse = reactive({x: 0, y: 0})
const selectedOffer = ref({})
const sourceOffer = ref({})
const targetOffer = ref({})
const selectedTracker = ref({})
const keyword = ref('')
const filteredOffers = computed(() =>
    watchdogStore.getFilteredOffers(offers.value, keyword.value)
)
const isShowFiltersModal = ref(false);
const expandedOffers = ref({})

const isExpanded = (offerID) => expandedOffers.value[offerID] ?? false

const toggleExpand = (offerID) => {
  expandedOffers.value[offerID] = !isExpanded(offerID)
}

const allExpanded = computed(() => offers.value.every(o => isExpanded(o.offerID)))

const toggleAll = () => {
  const newState = !allExpanded.value
  offers.value.forEach(offer => {
    expandedOffers.value[offer.offerID] = newState
  })
}

const hasError = (value) => Number(value || 0) !== 0

const hasTrackerError = (offer) => {
  return offer.trackers?.some(tracker => hasError(tracker.consecutiveErrors))
}

const getOfferCardClass = (offer) => {
  if (hasError(offer.consecutiveErrors)) {
    return 'bg-red-950/70 border-red-500/80 shadow-[0_0_24px_rgba(239,68,68,0.28)]'
  }

  if (hasTrackerError(offer)) {
    return 'bg-yellow-950/45 border-yellow-500/80 shadow-[0_0_22px_rgba(234,179,8,0.22)]'
  }

  return 'bg-slate-800 border-slate-600'
}

const getTrackerCardClass = (element) => {
  if (hasError(element.consecutiveErrors)) {
    return 'bg-red-950/75 border border-red-500/80 text-red-100 shadow-[0_0_18px_rgba(239,68,68,0.22)]'
  }

  if (cutItem.value?.trackerID === element.trackerID) {
    return 'bg-slate-900 border border-yellow-300 text-yellow-300'
  }

  return 'bg-slate-900 border border-slate-600 hover:border-blue-400 hover:text-blue-400 text-gray-200'
}

const getTrackersCountClass = (offer) => {
  if (hasError(offer.consecutiveErrors)) {
    return 'bg-red-900/80 border border-red-400/70 text-red-100 shadow-[0_0_16px_rgba(239,68,68,0.22)]'
  }

  if (hasTrackerError(offer)) {
    return 'bg-yellow-900/75 border border-yellow-400/70 text-yellow-100 shadow-[0_0_16px_rgba(234,179,8,0.22)]'
  }

  return 'bg-slate-900 text-gray-400'
}

const loadOffers = async (showLoading = true) => {
  if (showLoading) loading.value = true

  try {
    const res = await watchdogStore.action_getOffers()
    if (res.result === true) {
      offers.value = [...res.data]
    }
  } catch (e) {
    console.error(e)
  } finally {
    if (showLoading) loading.value = false
    buildLayout()
  }
}
const fetchInitialData = async () => {
  await loadOffers(true)
}
const refreshData = async () => {
  if (isFetching.value) return

  isFetching.value = true
  try {
    await loadOffers(false)
  } finally {
    isFetching.value = false
  }
}
const startPolling = () => {
  if (pollInterval.value) return
  pollInterval.value = setInterval(refreshData, 2000)
}
const stopPolling = () => {
  if (pollInterval.value) {
    clearInterval(pollInterval.value)
    pollInterval.value = null
  }
}

function updateColumnsCount() {
  const w = window.innerWidth
  if (w <= 640) columnsCount.value = 1
  else if (w <= 768) columnsCount.value = 1
  else if (w <= 1024) columnsCount.value = 2
  else if (w <= 1280) columnsCount.value = 2
  else if (w <= 1536) columnsCount.value = 3
  else columnsCount.value = 4
}

function buildLayout() {
  const baseList = filteredOffers.value

  const sortedList = [...baseList].sort(
      (a, b) =>
          (b.trackers.length || 1) - (a.trackers.length || 1)
  )

  const cols = Array.from({length: columnsCount.value}, () => [])
  const totals = Array.from({length: columnsCount.value}, () => 0)

  sortedList.forEach(offer => {
    const offerSize = offer.trackers.length > 0 ? offer.trackers.length : 1
    const minIndex = totals.indexOf(Math.min(...totals))
    cols[minIndex].push(offer)
    totals[minIndex] += offerSize
  })

  columns.value = cols
}

const swapTracker = async (targetOffer) => {
  if (!cutItem.value || targetOffer.offerID === cutItem.value.offerID) return

  const sourceOffer = offers.value.find(o => o.offerID === cutItem.value.offerID)
  if (!sourceOffer) return

  stopPolling()
  isCutSwapPending.value = true
  confirmActionTaken.value = false

  confirm.require({
    group: 'templating',
    header: 'Confirmation',
    message: {
      title: `Do you want to swap this tracker? From ${cutItem.value.offerID} To ${targetOffer.offerID}`,
      description: `From ${cutItem.value.offerID} To ${targetOffer.offerID}`,
      text: 'Are you sure?',
    },
    icon: 'pi pi-exclamation-circle',
    rejectProps: {
      class: '!border-slate-500 !py-2 !px-4 !text-slate-300',
      label: 'Cancel',
      outlined: true,
      size: 'small'
    },
    acceptProps: {
      class: '!border-blue-500 !py-2 !px-4 !text-blue-500 hover:!bg-blue-500 hover:!text-slate-200',
      outlined: true,
      label: 'Swap',
      size: 'small'
    },

    onHide: handleCutSwapConfirmHide,

    accept: async () => {
      confirmActionTaken.value = true

      try {
        const res = await watchdogStore.action_swapTracker({
          "offerID": cutItem.value.offerID,
          "trackerID": cutItem.value.trackerID,
          "newOfferID": targetOffer.offerID
        })

        if (res.result === true) {
          showMessage('Tracker Swapped.', 'success');

          sourceOffer.trackers = sourceOffer.trackers.filter(
              l => l.trackerID !== cutItem.value.trackerID
          )
          targetOffer.trackers.push({
            title: cutItem.value.title,
            offerID: targetOffer.offerID,
            trackerID: cutItem.value.trackerID,
            store: cutItem.value.store,
            price: cutItem.value.price,
            currency: cutItem.value.currency
          })
        } else {
          showMessage(res, 'error')
        }
      } catch (err) {
        showMessage(err, 'error')
      } finally {
        cutItem.value = null
        isCutSwapPending.value = false
        startPolling()
      }
    },

    reject: () => {
      confirmActionTaken.value = true

      cutItem.value = null
      showMessage('Swap canceled.', 'info')

      isCutSwapPending.value = false
      startPolling()
    }
  })
}

function updateMouse(e) {
  mouse.x = e.clientX + 10
  mouse.y = e.clientY + 10
}

function openAddOfferModal() {
  selectedOffer.value = null
  isShowAddOfferModal.value = true
}

const showFiltersModal = () => {
  isShowFiltersModal.value = true
}
const addTracker = async (offer) => {
  try {
    const res = await watchdogStore.action_addTracker({
      "offerID": offer.offerID,
      "trackerID": tracker.trackerID
    })
    if (res.result === true) {
      await fetchInitialData()
    }
  } catch (e) {

  } finally {

  }
}
const onDragStart = (evt, offer) => {
  const element = evt.item?._underlying_vm_ || evt.draggedContext?.element
  //console.log('Dragging started:')

  sourceOffer.value = offer
  selectedTracker.value = element

  pendingDragConfirm.value = false
  stopPolling()
}
const onMove = (evt, offer) => {
  //console.log('Moving...')
}
const onEnd = (evt, offer) => {
  //console.log('Dragging ended:')

  if (!pendingDragConfirm.value) {
    startPolling()
  }
}
const onAdd = (evt, targetOfferParam) => {
  //console.log('onAdd in target side')

  targetOffer.value = targetOfferParam

  const draggedTracker = evt.item?._underlying_vm_ || evt.clone || evt.draggedContext?.element

  draggedItem.value = draggedTracker
  dragOldIndex.value = evt.oldIndex ?? 0
  pendingDragConfirm.value = true
  confirmActionTaken.value = false

  confirm.require({
    group: 'templating',
    header: 'Confirmation',
    message: {
      title: `Do you want to swap this tracker? From ${sourceOffer.value.offerID} To ${targetOffer.value.offerID}`,
      text: 'Are you sure?'
    },
    icon: 'pi pi-exclamation-circle',
    rejectProps: {
      class: '!border-slate-500 !py-2 !px-4 !text-slate-300',
      label: 'Cancel',
      outlined: true,
      size: 'small'
    },
    acceptProps: {
      class: '!border-blue-500 !py-2 !px-4 !text-blue-500 hover:!bg-blue-500 hover:!text-slate-200',
      outlined: true,
      label: 'Swap',
      size: 'small'
    },

    onHide: handleConfirmHide,

    accept: async () => {
      confirmActionTaken.value = true

      try {
        const res = await watchdogStore.action_swapTracker({
          "offerID": sourceOffer.value.offerID,
          "trackerID": selectedTracker.value.trackerID,
          "newOfferID": targetOffer.value.offerID
        })
        if (res.result === true) {
          showMessage('Tracker Swapped.', 'success')
          await refreshData()
        } else {
          showMessage(res, 'error')
          await refreshData()
        }
      } catch (err) {
        showMessage(err, 'error')
        await refreshData()
      } finally {
        pendingDragConfirm.value = false
        startPolling()
      }
    },
    reject: () => {
      confirmActionTaken.value = true

      revertDrag()
      showMessage('Swap canceled.', 'info')

      pendingDragConfirm.value = false
      startPolling()
    }
  })
}
const revertDrag = () => {
  const source = offers.value.find(o => o.offerID === sourceOffer.value.offerID)
  const target = offers.value.find(o => o.offerID === targetOffer.value.offerID)

  if (source && target && draggedItem.value) {
    target.trackers = target.trackers.filter(t => t.trackerID !== draggedItem.value.trackerID)
    source.trackers.splice(dragOldIndex.value, 0, draggedItem.value)
  }
}
const handleConfirmHide = () => {
  if (pendingDragConfirm.value && !confirmActionTaken.value) {
    //console.log('Confirm dismissed externally → reverting drag')

    revertDrag()
    showMessage('Swap canceled.', 'info')

    pendingDragConfirm.value = false
    startPolling()
  }

  confirmActionTaken.value = false
}
const handleCutSwapConfirmHide = () => {
  if (isCutSwapPending.value && !confirmActionTaken.value) {
    //console.log('Cut-swap confirm dismissed externally')

    cutItem.value = null
    showMessage('Swap canceled.', 'info')

    isCutSwapPending.value = false
    startPolling()
  }

  confirmActionTaken.value = false
}

const offerMenus = reactive({})
const trackerMenus = reactive({})
const getOfferMenuItems = (offer) => [
  {label: 'Add New Tracker', icon: 'pi pi-pencil', command: () => openAddTrackerModal(offer)},
  {label: 'Edit', icon: 'pi pi-trash', command: () => updateOffer(offer)},
  {label: 'Delete', icon: 'pi pi-trash', command: () => removeOffer(offer)},
  {label: 'Open Management Link', icon: 'pi pi-trash', command: () => openOfferManagementLink(offer)},
  {label: 'Open Public Link', icon: 'pi pi-trash', command: () => openOfferPublicLink(offer)},
]
const getTrackerMenuItems = (offer, tracker) => [
  {label: 'Remove', icon: 'pi pi-trash', command: () => removeTracker(offer, tracker)},
  {label: 'Swap Parent', icon: 'pi pi-pencil', command: () => toggleCut(offer, tracker)},
  {label: 'Open Public Link', icon: 'pi pi-trash', command: () => openTrackerPublicLink(tracker)},
]
const openAddTrackerModal = (offer) => {
  selectedOffer.value = offer
  isShowAddTrackerModal.value = true
}

const updateOffer = (offer) => {
  selectedOffer.value = offer
  isShowAddOfferModal.value = true
}
const removeOffer = async (offer) => {
  confirm.require({
    group: 'templating',
    header: 'Confirmation',
    message: {
      title: `Do you want to remove this offer?`,
      text: 'Are you sure?'
    },
    icon: 'pi pi-exclamation-circle',
    rejectProps: {
      class: '!border-slate-500 !py-2 !px-4 !text-slate-300',
      label: 'Cancel',
      outlined: true,
      size: 'small'
    },
    acceptProps: {
      class: '!border-blue-500 !py-2 !px-4 !text-blue-500 hover:!bg-blue-500 hover:!text-slate-200',
      outlined: true,
      label: 'Remove',
      size: 'small'
    },
    accept: async () => {
      try {
        setDeleting(true)
        const res = await watchdogStore.action_removeOffer({"offerID": offer.offerID})
        if (res.result === true) {
          showMessage('Offer Removed.', 'success');
          await fetchInitialData()
        } else {
          showMessage(res, 'error');
        }
      } catch (err) {
        showMessage(err, 'error');
      } finally {
        setDeleting(false)
      }
    },
    reject: () => {
      return
    }
  });
}
const toggleCut = (sourceOffer, linked) => {
  if (cutItem.value?.trackerID === linked.trackerID) {
    cutItem.value = null
  } else {
    cutItem.value = {
      title: linked.title,
      offerID: sourceOffer.offerID,
      trackerID: linked.trackerID,
      store: linked.store,
      price: linked.price,
      currency: linked.currency
    }
  }
}
const removeTracker = (offer, tracker) => {
  confirm.require({
    group: 'templating',
    header: 'Confirmation',
    message: {
      title: `Do you want to remove this tracker?`,
      text: 'Are you sure?'
    },
    icon: 'pi pi-exclamation-circle',
    rejectProps: {
      class: '!border-slate-500 !py-2 !px-4 !text-slate-300',
      label: 'Cancel',
      outlined: true,
      size: 'small'
    },
    acceptProps: {
      class: '!border-blue-500 !py-2 !px-4 !text-blue-500 hover:!bg-blue-500 hover:!text-slate-200',
      outlined: true,
      label: 'Remove',
      size: 'small'
    },
    accept: async () => {
      try {
        setDeleting(true)
        const res = await watchdogStore.action_removeTracker({
          "offerID": offer.offerID,
          "trackerID": tracker.trackerID
        })
        if (res.result === true) {
          showMessage('Tracker Removed.', 'success');
          await fetchInitialData()
        } else {
          showMessage(res, 'error');
        }
      } catch (err) {
        showMessage(err, 'error');
      } finally {
        setDeleting(false)
      }
    },
    reject: () => {
      return
    }
  });
}

function getTimePassed(lastPublish) {
  const now = Date.now()
  const diff = now - lastPublish
  const minutes = Math.floor(diff / 60000)

  if (minutes < 1) return 'just now'
  if (minutes === 1) return `${minutes} min ago`
  if (minutes < 60) return `${minutes} mins ago`

  const hours = Math.floor(minutes / 60)
  return `${hours} hour${hours > 1 ? 's' : ''} ago`
}

const openOfferManagementLink = (offer) => {
  window.open(offer.managementLink, '_blank')
}
const openOfferPublicLink = (offer) => {
  window.open(offer.offerLink, '_blank')
}
const openTrackerPublicLink = (tracker) => {
  window.open(tracker.trackerLink, '_blank')
}
const toggleOfferMenu = (event, id) => {
  offerMenus[id]?.toggle(event)
}
const toggleTrackerMenu = (event, offerID, trackerID) => {
  const key = `${offerID}-${trackerID}`
  const menu = trackerMenus[key]
  if (menu) {
    menu.toggle(event)
  }
}

window.addEventListener("mousemove", updateMouse)

watch(
    () => watchdogStore.filter,
    () => buildLayout(),
    {deep: true}
)
watch(columnsCount, buildLayout)
watch(keyword, buildLayout)

onMounted(async () => {
  await fetchInitialData()
  updateColumnsCount()
  buildLayout()

  window.addEventListener("resize", updateColumnsCount)
  window.addEventListener("mousemove", updateMouse)

  startPolling()
})

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateColumnsCount)
  window.removeEventListener("mousemove", updateMouse)

  stopPolling()
})

provide('isShowAddOfferModal', isShowAddOfferModal)
provide('isShowAddTrackerModal', isShowAddTrackerModal)
provide('isShowFiltersModal', isShowFiltersModal)
</script>

<template>
  <div class="h-full">
    <div class="flex flex-col gap-2 h-full overflow-hidden">
      <div class="flex flex-row justify-between gap-4 w-full">
        <Breadcrumbs/>
        <div class="flex flex-row gap-2 justify-end">
          <div
              class="flex flex-row gap-2 items-center w-max h-[32px] bg-slate-900 border border-slate-700 text-slate-300 px-2 rounded-md">
            <span>
              <svg width="16" height="16" viewBox="0 0 24 24">
                <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                      d="m21 21l-4.343-4.343m0 0A8 8 0 1 0 5.343 5.343a8 8 0 0 0 11.314 11.314"/>
              </svg>
            </span>
            <input v-model="keyword" type="text"
                   class="bg-transparent w-full block focus:outline-none h-full rounded-md text-sm">
            <button v-if="keyword"
                    @click="keyword = ''" class="hover:text-red-400">
              <svg width="16" height="16" viewBox="0 0 16 16">
                <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                      d="m11.25 4.75l-6.5 6.5m0-6.5l6.5 6.5"/>
              </svg>
            </button>
          </div>
          <button @click="showFiltersModal" class="btn btn-outline-dark py-1 text-xs font-medium rounded-md h-[32px]"
                  :class="{'border !border-blue-500 shadow-[0_0_20px_5px_rgba(59,130,246,0.6)]': watchdogStore.isFilterActive}">
            Filters {{ watchdogStore.isFilterActive ? '(' + watchdogStore.activeFilterCount + ')' : '' }}
          </button>
          <button @click="openAddOfferModal"
                  class="btn btn-outline-dark py-1 text-xs font-medium rounded-md flex items-center h-[32px]">
            Add New Offer
          </button>
          <button @click="toggleAll"
                  class="btn btn-outline-dark py-1 text-xs font-medium rounded-md flex items-center h-[32px]">
            {{ allExpanded ? 'Collapse All' : 'Expand All' }}
          </button>
        </div>
      </div>
      <div v-if="loading" class="flex items-center justify-center h-60">
        <Icon icon="eos-icons:bubble-loading" class="size-12"/>
      </div>
      <div v-else class="flex-1 flex gap-8 justify-center items-start overflow-y-auto pt-4 px-2 custom-scrollbar">
        <div
            v-for="(col, cIndex) in columns"
            :key="cIndex"
            class="flex flex-col gap-8 flex-1"
        >
          <div
              v-for="(offer, index) in col"
              :key="offer.offerID"
              class="text-white p-2 rounded-xl shadow transition-all flex flex-col gap-4 relative border"
              :class="getOfferCardClass(offer)"
          >
            <div class="absolute -top-4 -left-2 ">
              <div v-if="offer.store === 'Incepto USD'"
                   v-tippy="{ content: offer.store, theme: 'my-theme', placement: 'right' }"
                   class="group">
                <img src="../../../assets/images/incepto.png" alt="" class="border-2 border-slate-600 size-8 rounded-full">
              </div>
              <div v-else v-tippy="{ content: offer.store, theme: 'my-theme', placement: 'right' }"
                   class="group">
                <img src="../../../assets/images/dealhub.png" alt="" class="border-2 border-slate-600 size-8 rounded-full">
              </div>
            </div>
            <div class="flex flex-row gap-2 text-sm pt-2">
              <div class="flex-1 flex flex-col justify-between gap-4 text-sm">
                <div class="flex flex-row justify-between gap-2 items-start">
                  <p class="text-blue-400">{{ offer.title }}</p>
                  <div class="flex flex-col gap-1 text-white text-xs">
                    <button @click="toggleOfferMenu($event, offer.offerID)">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                           stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                           class="lucide lucide-ellipsis-vertical-icon lucide-ellipsis-vertical">
                        <circle cx="12" cy="12" r="1"/>
                        <circle cx="12" cy="5" r="1"/>
                        <circle cx="12" cy="19" r="1"/>
                      </svg>
                    </button>
                    <button @click="toggleExpand(offer.offerID)">
                      <svg v-if="isExpanded(offer.offerID)" width="16" height="16" viewBox="0 0 24 24" fill="none"
                           stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                           class="lucide lucide-chevron-down">
                        <path d="m6 9 6 6 6-6"/>
                      </svg>
                      <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                           stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                           class="lucide lucide-chevron-right">
                        <path d="m9 18 6-6-6-6"/>
                      </svg>
                    </button>

                    <Menu
                        :ref="el => (offerMenus[offer.offerID] = el)"
                        :model="getOfferMenuItems(offer)"
                        :popup="true"
                        class="text-xs"
                    />
                  </div>
                </div>
                <div class="flex flex-col gap-1 text-xs">
                  <div class="flex flex-row justify-between">
                    <p>{{ offer.game }}</p>
                    <p v-if="offer.consecutiveErrors !== 0" class="watchdog-stale-time">
                      {{ offer.lastUpdate ? getTimePassed(offer.lastUpdate) : '' }}</p>
                  </div>
                  <div class="flex flex-row justify-between">
                    <p>#{{ offer.offerID }}</p>
                    <p>{{ offer.price }} $ &nbsp {{ offer.currency }}</p>
                  </div>
                </div>
              </div>
            </div>
            <!-- Draggable linked offers -->
            <draggable
                v-model="offer.trackers"
                group="trackers"
                item-key="trackerID"
                :component-data="{ name: 'fade', type: 'transition' }"
                class="flex flex-col gap-2 rounded-xl"
                @start="(e) => onDragStart(e, offer)"
                @end="(e) => onEnd(e, offer)"
                :move="(e) => onMove(e, offer)"
                @add="(e) => onAdd(e, offer)"
                handle=".handle"
            >
              <template #item="{ element }">
                <div v-show="isExpanded(offer.offerID)" :class="[
                      'text-sm p-2 rounded-lg flex flex-col justify-between gap-4 items transition',
                      getTrackerCardClass(element)
                    ]"
                >
                  <div class="flex flex-row gap-1 items-start justify-between">
                    <div class="flex flex-row gap-1">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                           stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                           class="lucide lucide-grip-vertical-icon lucide-grip-vertical handle cursor-move">
                        <circle cx="9" cy="12" r="1"/>
                        <circle cx="9" cy="5" r="1"/>
                        <circle cx="9" cy="19" r="1"/>
                        <circle cx="15" cy="12" r="1"/>
                        <circle cx="15" cy="5" r="1"/>
                        <circle cx="15" cy="19" r="1"/>
                      </svg>
                      <p class="flex-1 select-text">{{ element.title }}</p>
                    </div>
                    <div class="flex flex-col gap-1">
                      <!--                    <button @click.stop="removeTracker(offer, element)"
                                                  class="text-white hover:text-red-500 hover:font-bold">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                                 class="lucide lucide-x-icon lucide-x">
                                              <path d="M18 6 6 18"/>
                                              <path d="m6 6 12 12"/>
                                            </svg>
                                          </button>
                                          <button @click.stop="toggleCut(offer, element)"
                                                  class="text-white hover:text-red-500 hover:font-bold">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                                 class="lucide lucide-scissors-icon lucide-scissors">
                                              <circle cx="6" cy="6" r="3"/>
                                              <path d="M8.12 8.12 12 12"/>
                                              <path d="M20 4 8.12 15.88"/>
                                              <circle cx="6" cy="18" r="3"/>
                                              <path d="M14.8 14.8 20 20"/>
                                            </svg>
                                          </button>-->
                      <button @click="toggleTrackerMenu($event, offer.offerID, element.trackerID)">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                             class="lucide lucide-ellipsis-vertical-icon lucide-ellipsis-vertical">
                          <circle cx="12" cy="12" r="1"/>
                          <circle cx="12" cy="5" r="1"/>
                          <circle cx="12" cy="19" r="1"/>
                        </svg>
                      </button>
                      <Menu
                          :ref="el => (trackerMenus[`${offer.offerID}-${element.trackerID}`] = el)"
                          :model="getTrackerMenuItems(offer, element)"
                          :popup="true"
                          class="text-xs"
                      />
                    </div>
                  </div>
                  <div class="flex flex-col gap-1 text-xs">
                    <div class="flex flex-row justify-between">
                      <p>{{ element.store?.split(' USD')[0] }}</p>
                      <p v-if="element.consecutiveErrors !== 0" class="watchdog-stale-time">
                        {{ element.lastUpdate ? getTimePassed(element.lastUpdate) : '' }}</p>
                    </div>
                    <div class="flex flex-row justify-between">
                      <p>#{{ element.trackerID }}</p>
                      <p>{{ element.price }} $ &nbsp {{ element.currency }}</p>
                    </div>
                  </div>
                </div>
              </template>

              <template #footer>
                <div
                    v-if="isExpanded(offer.offerID) && offer.trackers.length === 0"
                    class="text-gray-400 text-sm italic text-center pt-2 pb-3 select-none bg-slate-900 rounded-lg"
                >
                  Drop or Paste here
                </div>
                <div
                    v-else-if="!isExpanded(offer.offerID)"
                    class="text-sm italic text-center pt-2 pb-3 select-none rounded-lg cursor-pointer transition"
                    :class="getTrackersCountClass(offer)"
                    @click="toggleExpand(offer.offerID)"
                >
                  {{ offer.trackers.length }} tracker{{ offer.trackers.length === 1 ? '' : 's' }}
                </div>
              </template>
            </draggable>
            <div v-if="cutItem && cutItem.offerID !== offer.offerID" @click="swapTracker(offer)"
                 class="w-full h-full absolute top-0 left-0 cursor-pointer"></div>
          </div>
        </div>
      </div>
      <!-- Floating label near mouse -->
      <div
          v-if="cutItem"
          class="fixed z-[9999] bg-slate-800 text-white p-2 rounded-lg shadow pointer-events-none w-60"
          :style="{ top: mouse.y + 'px', left: mouse.x + 'px' }"
      >
        <div
            class="text-xs rounded-lg flex flex-col justify-between gap-2 items cursor-pointer transition select-none text-yellow-300'">
          <div class="flex flex-row gap-1 items-start justify-between">
            <p class="">{{ cutItem.title }}</p>
          </div>
          <div class="flex flex-row justify-between">
            <p class="">{{ cutItem.store?.split(' USD')[0] }}</p>
          </div>
          <div class="flex flex-row justify-between">
            <p>{{ cutItem.trackerID }}</p>
            <p>{{ cutItem.price }} $ &nbsp {{ cutItem.currency }}</p>
          </div>
        </div>
      </div>
      <AddOffer v-if="isShowAddOfferModal" :offer="selectedOffer" @refresh="fetchInitialData"/>
      <AddTracker v-if="isShowAddTrackerModal" :offer="selectedOffer" @refresh="fetchInitialData"/>
      <FiltersModal v-if="isShowFiltersModal"/>
    </div>
  </div>
</template>

<style>
.tippy-box[data-theme~='my-theme'] {
  @apply bg-slate-900 text-sm p-2 text-white;
}

.watchdog-stale-time {
  color: #f87171;
  background: rgba(127, 29, 29, 0.28);
  border: 1px solid rgba(248, 113, 113, 0.35);
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
}
</style>